import { IFormulaExpressionEvaluator } from '@dpr-details/models';
import { evaluateArithmeticTokens } from '@shared/utils';
import { FormulaBlankOperand } from './formula.utils';

/** Default formula evaluator: arithmetic only. Stateless and safe to share. */
export class ArithmeticFormulaExpressionEvaluator
  implements IFormulaExpressionEvaluator
{
  evaluate(expression: string): number {
    const tokens = ArithmeticFormulaExpressionEvaluator.tokenize(expression);
    const result =
      tokens === null
        ? null
        : evaluateArithmeticTokens(
            tokens,
            ArithmeticFormulaExpressionEvaluator.toNumber,
            (token) => token === FormulaBlankOperand
          );

    if (result === null) {
      throw new Error(`Malformed formula expression: '${expression}'`);
    }

    return result;
  }

  /**
   * Splits an arithmetic string into number / blank / operator / paren tokens,
   * or null if it contains anything else. A numeric literal may carry the
   * decimal point on either side of its digits but no exponent: the API and the
   * database read 1e3 as no number, so the cell stays blank here too.
   */
  private static tokenize(expression: string): string[] | null {
    const tokens: string[] = [];
    const scanner = /(\d+\.?\d*|\.\d+)|([+\-*/()_])|(\s+)/g;
    let expectedIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = scanner.exec(expression)) !== null) {
      // A gap between matches means an unsupported character sat there.
      if (match.index !== expectedIndex) {
        return null;
      }
      expectedIndex = scanner.lastIndex;

      if (match[3] === undefined) {
        tokens.push(match[1] ?? match[2]);
      }
    }

    return expectedIndex === expression.length ? tokens : null;
  }

  private static toNumber(token: string): number | null {
    const value = parseFloat(token);

    return Number.isNaN(value) ? null : value;
  }
}
